import "./saved-commodities-modal.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useState } from "react";

import { CommoditiesGrid } from "~/features/commodities/components/commodities-grid/commodities-grid.tsx";
import { CommoditiesMobile } from "~/features/commodities/components/commodities-mobile/commodities-mobile.tsx";
import { NewCommoditiesModal } from "~/shared/components/commodities/new-commodities-modal.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";

type SavedCommoditiesModalProps = {
	/** Shared by Quote, Template and Shipment workflows; no quote context is required. */
	readonly customerId: number | undefined;
	readonly onToggleModal: () => void;
	readonly onSetSelectedItems: (rows: Commodity[]) => void;
	readonly showModal: boolean;
};

export const SavedCommoditiesModal = ({
	showModal,
	...props
}: SavedCommoditiesModalProps) =>
	showModal ? (
		<SavedCommoditiesModalContent key={props.customerId} {...props} />
	) : null;

const SavedCommoditiesModalContent = ({
	customerId,
	onToggleModal,
	onSetSelectedItems,
}: Omit<SavedCommoditiesModalProps, "showModal">) => {
	// Same flag that gates the Commodities page card view, so both roll out together.
	const { isEnabled: isMobileViewEnabled } = useFeatureFlagGate(
		"enableTraxCommoditiesMobileView",
	);
	const isMobile = useIsMobile() && isMobileViewEnabled;
	const [showNewCommoditiesModal, setShowNewCommoditiesModal] = useState(false);
	const [selectedItems, setSelectedItems] = useState<Commodity[]>([]);
	const [isEditMode, setIsEditMode] = useState(false);
	const desktopWidth = isEditMode ? "85%" : "70%";

	const handleAddSelectedAndClose = useCallback(() => {
		if (selectedItems.length === 0) return;
		onSetSelectedItems(selectedItems);
		onToggleModal();
	}, [selectedItems, onSetSelectedItems, onToggleModal]);

	const handleAddAndClose = useCallback(
		(dataItem: Commodity) => {
			onSetSelectedItems([dataItem]);
			onToggleModal();
		},
		[onSetSelectedItems, onToggleModal],
	);

	return (
		<>
			<TraxModal
				className={"saved-commodities-modal"}
				onClose={onToggleModal}
				title={"Saved Commodities"}
				width={isMobile ? "100%" : desktopWidth}
			>
				{isMobile ? (
					<CommoditiesMobile
						customerId={String(customerId ?? 0)}
						isModal={true}
						onSetSelectedItems={setSelectedItems}
					/>
				) : (
					<CommoditiesGrid
						customerId={String(customerId ?? 0)}
						isModal={true}
						onAddAndClose={handleAddAndClose}
						onEditModeChange={setIsEditMode}
						onSetSelectedItems={setSelectedItems}
					/>
				)}
				<DialogActionsBar>
					<div className={"saved-commodities-action-bar"}>
						<div className={"add-new-commodities-wrapper"}>
							<Button
								onClick={() => setShowNewCommoditiesModal(true)}
								variant={isMobile ? "secondary" : "primary"}
							>
								Add New Commodity
							</Button>
						</div>

						<Button
							disabled={selectedItems.length === 0}
							onClick={handleAddSelectedAndClose}
							variant={"primary"}
						>
							Add Selected ({selectedItems.length}) & Close
						</Button>
						<Button onClick={onToggleModal}>Cancel</Button>
					</div>
				</DialogActionsBar>
			</TraxModal>
			{showNewCommoditiesModal ? (
				<NewCommoditiesModal
					customerId={customerId}
					onClose={() => setShowNewCommoditiesModal(false)}
				/>
			) : null}
		</>
	);
};
